import type {
	HectaresInputGroup,
	InputGroup,
	ModuleSchema,
	T2Schema,
	StartWithWithout,
	StartWithWithoutGeneric,
	Option,
} from "@typedefs/modulesInterfaces";
import type { LandUseChangeResponse } from "./landUseChangeTypes";
import { FieldType } from "@typedefs/modulesInterfaces";
import { BEModulesClassNames } from "@utils/moduleList";
import * as Yup from "yup";
import { ModuleTypeRegistry } from "@utils/moduleTypeRegistry";

export const includedPropList = [
	"id",
	"start_year",
	"soc_t2_start",
	"soc_t2_w",
	"soc_t2_wo",
	"area",
	"note",
	"is_fire_used_start",
	"is_fire_used_w",
	"is_fire_used_wo",
	"dry_matter_w",
	"dry_matter_wo",
	"module_type_start",
	"module_type_w",
	"module_type_wo",
	"module_type_thread",
	"is_fire_used_thread",
	"dry_matter_thread",
];

export interface Module {
	hectares: number | undefined;
	typeOfLandUse: StartWithWithout;
	fireUse: {
		start: string;
		with: boolean;
		without: boolean;
	};
	dryMatterRemoved: StartWithWithout;
}

export const validationSchema = Yup.object({});
export const initialValues: LandUseChangeResponse = {
	id: undefined,
	area: null,
	module_type: null,
	note: null,
	start_year: null,
	soc_t2_start: null,
	soc_t2_w: null,
	soc_t2_wo: null,
	is_fire_used_w: false,
	is_fire_used_wo: false,
	dry_matter_w: null,
	dry_matter_wo: null,
	module_type_start: null,
	module_type_w: null,
	module_type_wo: null,
	activity: null,
	created_at: null,
	updated_at: null,
	is_fire_used_start: false,
	dry_matter_start: null,
	status: null,
};

const moduleRegistry = ModuleTypeRegistry.getInstance();

// Lazy evaluation functions that get IDs when called
const getModuleIdByClassName = (className: BEModulesClassNames) => moduleRegistry.getIdByClassName(className) ?? 0;

export const getMainActivityLandUseOptions = (): Option[] => {
	return [
		{ id: getModuleIdByClassName(BEModulesClassNames.ForestManagement), name: "Forest management" },
		{ id: getModuleIdByClassName(BEModulesClassNames.FloodedRice), name: "Flooded rice management" },
		{ id: getModuleIdByClassName(BEModulesClassNames.PerennialCropland), name: "Perennial cropland management" },
		{ id: getModuleIdByClassName(BEModulesClassNames.AnnualCropland), name: "Annual cropland management" },
		{ id: getModuleIdByClassName(BEModulesClassNames.CoastalWetlands), name: "Coastal wetland management" },
		{ id: getModuleIdByClassName(BEModulesClassNames.Settlements), name: "Infrastructure creation" },
		{ id: getModuleIdByClassName(BEModulesClassNames.SetAside), name: "Set land aside" },
		{ id: getModuleIdByClassName(BEModulesClassNames.LivestockManagement), name: "Livestock management" },
		{ id: getModuleIdByClassName(BEModulesClassNames.Waterbodies), name: "Water body management" },
		{ id: getModuleIdByClassName(BEModulesClassNames.LargeFisheries), name: "Large fisheries management" },
		{ id: getModuleIdByClassName(BEModulesClassNames.SmallFisheries), name: "Small fisheries management" },
		{ id: getModuleIdByClassName(BEModulesClassNames.Aquaculture), name: "Aquaculture" },
		{ id: getModuleIdByClassName(BEModulesClassNames.Grassland), name: "Grassland" },
		{
			id: getModuleIdByClassName(BEModulesClassNames.Energy),
			name: "Use of agricultural inputs, energy or irrigation",
		},
	];
};

export const getLandUseOptions = (): Option[] => {
	return [
		{ id: getModuleIdByClassName(BEModulesClassNames.ForestManagement), name: "Forest" },
		{ id: getModuleIdByClassName(BEModulesClassNames.FloodedRice), name: "Flooded rice" },
		{ id: getModuleIdByClassName(BEModulesClassNames.PerennialCropland), name: "Perennial cropland" },
		{ id: getModuleIdByClassName(BEModulesClassNames.AnnualCropland), name: "Annual cropland" },
		{ id: getModuleIdByClassName(BEModulesClassNames.CoastalWetlands), name: "Coastal Wetland" },
		{ id: getModuleIdByClassName(BEModulesClassNames.Settlements), name: "Settlements and infrastructure" },
		{ id: getModuleIdByClassName(BEModulesClassNames.SetAside), name: "Set aside land" },
		{ id: getModuleIdByClassName(BEModulesClassNames.Grassland), name: "Grassland" },
		{ id: getModuleIdByClassName(BEModulesClassNames.OtherLand), name: "Other land" },
	];
};

const getInitInputGroups: () => InputGroup[] = () => {
	const hectaresInputGroup: HectaresInputGroup = {
		type: FieldType.HECTARES,
		label: "module.hectares",
		unit: "[ha]",
		inputName: "module.area",
		disabled: true,
	};

	const landUse: StartWithWithoutGeneric = {
		type: FieldType.SWW_SELECT,
		inputName: "module.module_type",
		label: "module.type_of_land_use",
		startProps: { dropdownOptions: () => Promise.resolve({ data: getLandUseOptions(), isLoading: false, error: undefined }), disabled: true },
		withProps: { 
			dropdownOptions: () => Promise.resolve({ data: [...getMainActivityLandUseOptions(), { id: getModuleIdByClassName(BEModulesClassNames.OtherLand), name: "Other land" }], isLoading: false, error: undefined }), 
			disabled: true 
		},
		withoutProps: { dropdownOptions: () => Promise.resolve({ data: getLandUseOptions(), isLoading: false, error: undefined }), disabled: true },
	};

	const fireUse: StartWithWithoutGeneric = {
		type: FieldType.SWW,
		inputName: "module.is_fire_used",
		label: "module.fire_use_during_conversion",
		startProps: {
			inputType: "free",
			disabled: true,
		},
		withProps: {
			inputType: "boolean",
		},
		withoutProps: {
			inputType: "boolean",
		},
	};

	const dryMatterRemoved: StartWithWithoutGeneric = {
		type: FieldType.SWW,
		label: "module.dry_matter_removed_during_conversion",
		unit: "[t/ha]",
		inputName: "module.dry_matter",
		startProps: { disabled: true },
	};

	return [hectaresInputGroup, landUse, fireUse, dryMatterRemoved];
};

/* T2 */
export const t2InitialValues = {};
export const getSchemas = () => {
	const moduleSchema: ModuleSchema = {
		initInputGroups: getInitInputGroups(),
		mandatorySubmodules: [],
		optionalSubmodules: [],
	};
	const t2Schema: T2Schema = {
		submodules: [],
	};

	return { moduleSchema, t2Schema };
};
